import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// import { DashboardHeader } from './DashboardHeader';
import { AnalysisNav } from './ui/analysis-nav';
import { StarsBackground } from './ui/stars-background';
// Removed unused recharts imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Card } from "./ui/card";
// import { FounderEquityDilution } from './ui/equity-dilution';

type FundingStage = 'Pre-seed' | 'Seed' | 'Series A' | 'Series B';
// type AmountUnit = 'M'; // Unused type

interface FundingData {
  stage: FundingStage;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  average: number;
}

interface IndustryValuation {
  industry_name: string;
  subsector_name: string | null;
  caps: {
    raise_range: string;
    cap_value: number;
  }[];
}

interface ValuationProps {}

// Keeping only the used interface
interface EquityDilutionState {
  founderOwnership: number;
  optionPoolSize: number;
  raiseAmount: number;
  valuation: number;
  stage: FundingStage;
  targetDilution: number;
}

const fundingData: FundingData[] = [
  {
    stage: 'Pre-seed',
    p10: 0.1,
    p25: 0.25,
    p50: 0.5,
    p75: 0.75,
    p90: 1,
    average: 0.5
  },
  {
    stage: 'Seed',
    p10: 0.5,
    p25: 1,
    p50: 2,
    p75: 3.5,
    p90: 5,
    average: 2.4
  },
  {
    stage: 'Series A',
    p10: 5,
    p25: 8,
    p50: 12,
    p75: 18,
    p90: 25,
    average: 13.6
  },
  {
    stage: 'Series B',
    p10: 15,
    p25: 25,
    p50: 35,
    p75: 50,
    p90: 70,
    average: 39
  }
];

const formatValue = (value: number): string => {
  if (!value) return '-';
  
  // Value is already in millions from the backend
  // Format with 1 decimal place if it's not a whole number
  const formatted = value % 1 !== 0 ? value.toFixed(1) : value.toString();
  return `$${formatted}M`;
};

// Removed unused function

// Error function inverse approximation
const erfinv = (x: number): number => {
  const a = 0.147;
  const y = Math.log(1 - x * x);
  const z = 2 / (Math.PI * a) + y / 2;
  return Math.sign(x) * Math.sqrt(Math.sqrt(z * z - y / a) - z);
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const generateBellCurveData = (p10: number, p50: number, p90: number, userAmount: number) => {
  const points: Array<{
    percentile: number;
    amount: number;
    density: number;
    isUserPoint: boolean;
    label: string;
  }> = [];
  
  // Calculate statistical parameters first
  const mean = p50;
  const stdDev = (p90 - p10) / 2.56;
  
  // Generate points for each percentile from 0 to 100
  for (let percentile = 0; percentile <= 100; percentile++) {
    // Convert percentile to a value between 0 and 1
    const p = percentile / 100;
    
    // Approximate the inverse normal distribution
    const z = percentile === 0 ? -3.5 : 
             percentile === 100 ? 3.5 :
             Math.sqrt(2) * erfinv(2 * p - 1);
    const amount = mean + z * stdDev;
    
    // Calculate density using normal distribution formula
    const density = Math.exp(-Math.pow(z, 2) / 2) / (stdDev * Math.sqrt(2 * Math.PI));
    
    points.push({
      percentile,
      amount: Math.max(0, amount), // Ensure amount is not negative
      density: density * stdDev * 10, // Scale for better visualization
      isUserPoint: false,
      label: percentile === 10 ? 'p10' : 
             percentile === 50 ? 'p50' : 
             percentile === 90 ? 'p90' : ''
    });
  }
  
  // Add user point
  if (userAmount > 0) {
    // Find the closest percentile for the user amount
    const userPercentile = points.reduce((prev, curr) => 
      Math.abs(curr.amount - userAmount) < Math.abs(points[prev].amount - userAmount) ? 
      points.indexOf(curr) : prev, 0);
    points[userPercentile].isUserPoint = true;
  }
  
  return points;
};

// Custom tooltip component no longer needed with Chart.js

const Valuation: React.FC<ValuationProps> = () => {
  const { id } = useParams<{ id: string }>();
  const [userInputAmount, setUserInputAmount] = useState<number>(0);
  const [selectedStage, setSelectedStage] = useState<FundingStage>('Pre-seed');
  const [industryData, setIndustryData] = useState<IndustryValuation | null>(null);
  const [equityState, setEquityState] = useState<EquityDilutionState>({
    founderOwnership: 85,
    optionPoolSize: 15,
    raiseAmount: 0,
    valuation: 0,
    stage: 'Pre-seed',
    targetDilution: 20
  });
  const { api } = useAuth();

  useEffect(() => {
    const fetchIndustryData = async () => {
      if (!id) return;
      try {
        const response = await api.get(`/api/valuation-caps/${id}`);
        setIndustryData(response.data);
      } catch (error: any) {
        console.error('Error fetching industry data:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        setIndustryData(null);
      }
    };

    fetchIndustryData();
  }, [id, api]);

  // Removed unused functions

  const getTypicalDilution = (stage: FundingStage): { min: number; max: number } => {
    switch (stage) {
      case 'Pre-seed': return { min: 15, max: 25 };
      case 'Seed': return { min: 15, max: 25 };
      case 'Series A': return { min: 15, max: 20 };
      case 'Series B': return { min: 10, max: 15 };
      default: return { min: 15, max: 25 };
    }
  };

  const getHighlightedPercentileIndex = (amount: number, stageData: FundingData | undefined) => {
    if (!stageData || amount === 0) return -1;
    if (amount <= stageData.p10) return 0;
    if (amount <= stageData.p25) return 1;
    if (amount <= stageData.p50) return 2;
    if (amount <= stageData.p75) return 3;
    if (amount <= stageData.p90) return 4;
    return -1;
  };

  const calculatePercentile = (amount: number, stage: FundingStage): number => {
    const stageData = fundingData.find(d => d.stage === stage);
    if (!stageData) return 0;

    if (amount <= stageData.p10) return amount / stageData.p10 * 10;
    if (amount <= stageData.p25) return 10 + (amount - stageData.p10) / (stageData.p25 - stageData.p10) * 15;
    if (amount <= stageData.p50) return 25 + (amount - stageData.p25) / (stageData.p50 - stageData.p25) * 25;
    if (amount <= stageData.p75) return 50 + (amount - stageData.p50) / (stageData.p75 - stageData.p50) * 25;
    if (amount <= stageData.p90) return 75 + (amount - stageData.p75) / (stageData.p90 - stageData.p75) * 15;
    return 90 + Math.min((amount - stageData.p90) / stageData.p90 * 10, 10);
  };

  const userAmount = userInputAmount;

  // Get the selected stage data for the chart
  const selectedStageData = fundingData.find(d => d.stage === selectedStage);
  const highlightedIndex = getHighlightedPercentileIndex(userAmount, selectedStageData);

  const userPercentile = calculatePercentile(userAmount, selectedStage);

  // Define typical raise ranges for each stage - keeping for future reference
  /* const stageRangeMap = {
    'Pre-seed': ['<$250K', '$250–$499K'],
    'Seed': ['$500–$999K', '$1M–$2.4M'],
    'Series A': ['$1M–$2.4M', '$2.5M–$4.9M', '$5M–$9.9M'],
    'Series B': ['$5M–$9.9M', '>$10M']
  }; */

  // Generate bell curve data for the selected stage
  const curveData = selectedStageData ? generateBellCurveData(
    selectedStageData.p10,
    selectedStageData.p50,
    selectedStageData.p90,
    userAmount
  ) : [];

  // Calculate suggested valuation based on target dilution
  const calculateSuggestedValuation = (raiseAmount: number, targetDilution: number) => {
    if (targetDilution <= 0 || targetDilution >= 100) return 0;
    return (raiseAmount * (100 - targetDilution)) / targetDilution;
  };

  // Update equity state when raise amount or company stage changes
  useEffect(() => {
    setEquityState(prev => ({
      ...prev,
      raiseAmount: userAmount,
      stage: selectedStage as FundingStage,
      valuation: calculateSuggestedValuation(userAmount, prev.targetDilution)
    }));
  }, [userAmount, selectedStage]);

  // Removed unused handler

  return (
    <>
      <div className="min-h-screen bg-background relative overflow-x-hidden">
        {/* Stars Background */}
        <div className="fixed inset-0 -z-10">
          <StarsBackground />
        </div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* DashboardHeader is already included in the ProtectedRoute wrapper */}
          <AnalysisNav id={id || ''} />
          <div className="max-w-6xl mx-auto px-4 py-8">
            <Card className="p-6 relative bg-transparent border-none shadow-2xl backdrop-blur-sm">
              {/* Card Stars Background */}
              <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                <StarsBackground />
              </div>
              {/* Card Content */}
              <div className="relative z-10">
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: '"Open Sans", sans-serif' }}>Funding Round Analysis</h2>
        
        {/* Input Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-base font-medium mb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
              Your Raise Amount (in millions)
            </label>
            <input
              type="number"
              value={userInputAmount}
              onChange={(e) => setUserInputAmount(parseFloat(e.target.value) || 0)}
              className="w-full p-2 border rounded"
              placeholder="Enter amount in millions"
              step="0.1"
            />
          </div>
          <div>
            <label className="block text-base font-medium mb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
              Company Stage
            </label>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value as FundingStage)}
              className="w-full p-2 border rounded"
            >
              {fundingData.map(d => (
                <option key={d.stage} value={d.stage}>{d.stage}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Percentile Display */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>Your Position</h3>
          <p className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                Your raise of {formatValue(userAmount)} is in the{' '}
                <span className="font-bold text-gray-900" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  {userPercentile.toFixed(1)}th
                </span>{' '}
                percentile for {selectedStage} rounds
              </p>
            </div>

            {/* Typical Dilution Information */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>Typical Dilution</h3>
              <p className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                For {selectedStage} rounds, typical dilution is{' '}
                <span className="font-bold text-gray-900" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  {getTypicalDilution(selectedStage).min}-{getTypicalDilution(selectedStage).max}%
                </span>{' '}
                of equity
              </p>
              <p className="text-base text-gray-700 mt-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                Based on your raise amount of {formatValue(userAmount)}, 
                suggested valuation would be {formatValue(equityState.valuation)} 
                to achieve this dilution target
              </p>
            </div>

            {/* Bell Curve Visualization */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div style={{ width: '100%', height: 400 }}>
              {selectedStageData ? (
                <Line
                  data={{
                    labels: curveData.map(d => `${d.percentile}%`),
                    datasets: [
                      {
                        label: `${selectedStage} Distribution`,
                        data: curveData.map(d => d.density),
                        borderColor: '#F87171',
                        backgroundColor: 'rgba(248, 113, 113, 0.3)',
                        pointRadius: 0,
                        tension: 0.4,
                        fill: true
                      },
                      ...(userAmount > 0 ? [{
                        label: 'Your Raise Amount',
                        data: curveData.map(d => 
                          d.percentile === Math.round(userPercentile) ? d.density : null
                        ),
                        borderColor: '#2563EB',
                        backgroundColor: '#2563EB',
                        pointRadius: 6,
                        pointHoverRadius: 8,
                        fill: false,
                        showLine: false
                      }] : [])
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Percentile',
                          font: {
                            family: '"Open Sans", sans-serif',
                            size: 14
                          }
                        },
                        ticks: {
                          font: {
                            family: '"Open Sans", sans-serif'
                          }
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Distribution',
                          font: {
                            family: '"Open Sans", sans-serif',
                            size: 14
                          }
                        },
                        ticks: {
                          display: false
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          title: (items) => {
                            if (!items.length) return '';
                            const percentile = parseInt(items[0].label);
                            return `${percentile}th Percentile`;
                          },
                          label: (context) => {
                            const percentile = parseInt(context.label);
                            const value = curveData.find(d => d.percentile === percentile)?.amount || 0;
                            return `Amount: ${formatValue(value)}`;
                          }
                        },
                        titleFont: {
                          family: '"Open Sans", sans-serif',
                          size: 14
                        },
                        bodyFont: {
                          family: '"Open Sans", sans-serif',
                          size: 14
                        }
                      }
                    },
                    animation: {
                      duration: 1000
                    }
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  No data available for {selectedStage}
                </div>
              )}
              </div>
              {userAmount > 0 && (
                <div className="mt-2 text-center">
                  <span className="inline-flex items-center text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                    <span className="h-3 w-3 rounded-full bg-blue-600 mr-2"></span>
                    Your raise amount: {formatValue(userAmount)} ({userPercentile.toFixed(1)}th percentile)
                  </span>
                </div>
              )}
            </div>

            {/* Display percentile with industry context */}
            {userAmount > 0 && selectedStageData && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <p className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  Your raise amount of {formatValue(userAmount)} is{' '}
                  {userAmount < selectedStageData.p50 ? (
                    <React.Fragment>below the {selectedStage} median of {formatValue(selectedStageData.p50)}</React.Fragment>
                  ) : userAmount > selectedStageData.p50 ? (
                    <React.Fragment>above the {selectedStage} median of {formatValue(selectedStageData.p50)}</React.Fragment>
                  ) : (
                    <React.Fragment>at the {selectedStage} median of {formatValue(selectedStageData.p50)}</React.Fragment>
                  )}{' '}
                  for {industryData?.industry_name} companies.
                </p>
              </div>
            )}

            {/* Stage Details */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: '"Open Sans", sans-serif' }}>Typical Ranges for {selectedStage} - {industryData?.industry_name}({industryData?.subsector_name})</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['Low', 'Median', 'High'].map((range, index) => {
                  const value = index === 0 ? selectedStageData?.p25 : 
                                 index === 1 ? selectedStageData?.p50 : 
                                 index === 2 ? selectedStageData?.p75 : null;
                  return (
                    <div key={range} className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{range}</div>
                      <div className="text-2xl font-bold" style={{ fontFamily: '"Open Sans", sans-serif' }}>{value ? formatValue(value) : '-'}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <div className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>Typical Dilution at {selectedStage}</div>
                <div className="text-2xl font-bold" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  {getTypicalDilution(selectedStage).min}-{getTypicalDilution(selectedStage).max}%
                </div>
                <div className="text-base text-gray-700 mt-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  Expected equity dilution range for this stage
                </div>
              </div>
            </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Valuation;
